import { useId, useMemo, useState } from 'react';
import { cn, Textarea } from '@vit/ui';
import { Bold, Code, Italic, Link2, List, ListOrdered, Quote } from 'lucide-react';
import { renderMarkdown } from '../lib/markdown.js';
import { useTranslation } from '../lib/i18n.js';
import { submitOnModEnter } from '../lib/hotkeys.js';

export function MarkdownView({ source, className }: { source: string; className?: string }) {
  const html = useMemo(() => renderMarkdown(source), [source]);
  // Sanitized by DOMPurify with a strict allowlist (no raw HTML, no images).
  return <div className={cn('vit-markdown text-sm', className)} dangerouslySetInnerHTML={{ __html: html }} />;
}

const tools = [
  { icon: Bold, label: 'Bold', wrap: ['**', '**'] },
  { icon: Italic, label: 'Italic', wrap: ['_', '_'] },
  { icon: Code, label: 'Code', wrap: ['`', '`'] },
  { icon: Link2, label: 'Link', wrap: ['[', '](https://)'] },
  { icon: List, label: 'Bulleted list', prefix: '- ' },
  { icon: ListOrdered, label: 'Numbered list', prefix: '1. ' },
  { icon: Quote, label: 'Quote', prefix: '> ' },
] as const;

/**
 * Accessible Markdown editor: plain textarea with a formatting toolbar and sanitized preview.
 * Users never need Markdown knowledge for structured fields.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minRows = 5,
  onSubmit,
  id,
  invalid,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  onSubmit?: () => void;
  id?: string;
  invalid?: boolean;
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const autoId = useId();
  const textareaId = id ?? autoId;

  const apply = (tool: (typeof tools)[number]) => {
    const el = document.getElementById(textareaId) as HTMLTextAreaElement | null;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    let next: string;
    if ('wrap' in tool) next = value.slice(0, start) + tool.wrap[0] + selected + tool.wrap[1] + value.slice(end);
    else {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      next = value.slice(0, lineStart) + tool.prefix + value.slice(lineStart);
    }
    onChange(next);
    requestAnimationFrame(() => el.focus());
  };

  return (
    <div className="rounded-[var(--radius)] border border-border-strong bg-surface focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <button type="button" className={cn('rounded px-2 py-0.5 text-xs', mode === 'write' ? 'bg-muted font-medium text-fg' : 'text-fg-muted')} onClick={() => setMode('write')}>
          {t('common.write')}
        </button>
        <button type="button" className={cn('rounded px-2 py-0.5 text-xs', mode === 'preview' ? 'bg-muted font-medium text-fg' : 'text-fg-muted')} onClick={() => setMode('preview')}>
          {t('common.preview')}
        </button>
        {mode === 'write' ? (
          <div className="ml-auto flex items-center gap-0.5" role="toolbar" aria-label="Formatting">
            {tools.map((tool) => (
              <button key={tool.label} type="button" className="rounded p-1 text-fg-subtle hover:bg-muted hover:text-fg" aria-label={tool.label} title={tool.label} onClick={() => apply(tool)}>
                <tool.icon className="size-3.5" />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {mode === 'write' ? (
        <Textarea
          id={textareaId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          value={value}
          rows={minRows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => onSubmit && submitOnModEnter(e, onSubmit)}
          className="rounded-none border-0 shadow-none focus-visible:ring-0"
        />
      ) : (
        <div className="min-h-24 px-3 py-2">{value.trim() ? <MarkdownView source={value} /> : <p className="text-sm text-fg-subtle">—</p>}</div>
      )}
      <p className="border-t border-border px-2.5 py-1 text-[11px] text-fg-subtle">{t('common.markdownHint')}</p>
    </div>
  );
}
